import { routes } from "@constants/routes";
import { ArrowRightIcon, RankStarIcon, Typography, useFetchFile, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountContactTypeChoices,
  AuthAccountOrganizationEmployeeCooperationStatusChoices,
  AuthAccountOrganizationJobPositionLocationTypeChoices,
  GetEmployeesQuery,
} from "@graphql/generated";
import { Avatar, Button, Card, useDisclosure } from "@heroui/react";
import { getContactByType } from "@modules/Profile/InformationForm/hooks/useProfileFormData";
import dayjs from "dayjs";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChangeStatusModal } from "./ChangeStatusModal";

type EmployeeEdge = NonNullable<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetEmployeesQuery>["organization"]>["employee"]>["list"]
  >["edges"][number]
>;

interface EmployeeCardProps {
  employee: EmployeeEdge | null;
  layout: "LIST" | "DETAIL";
  refetch?: () => void;
}

interface ExpandableSectionProps {
  isExpanded: boolean;
}

// interface ActionButtonProps {
//   icon: React.ComponentType<{ className?: string; viewBox?: string }>;
//   tooltip: string;
//   isActive?: boolean;
//   onPress?: () => void;
// }

const ExpandableSection = ({ isExpanded }: ExpandableSectionProps) => (
  <div
    className={`
      w-full mt-4 border-t border-zinc-200 pt-4
      transition-all duration-300 ease-in-out
      ${isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0 hidden"}
    `}
  >
    <div className="space-y-2">
      {["Message content here"].map((message, index) => (
        <div key={index} className="p-3 bg-zinc-50 rounded-lg">
          <p className="text-sm text-zinc-700">{message}</p>
        </div>
      ))}
    </div>
  </div>
);

// const IconButton = ({ tooltip, isActive, onPress, ...rest }: ActionButtonProps) => (
//   <Tooltip content={tooltip} showArrow={true} classNames={{ content: "bg-zinc-200", arrow: "bg-zinc-200" }}>
//     <Button
//       isIconOnly
//       radius="full"
//       className={`
//         p-0 min-w-0 border border-zinc-300
//         transition-colors duration-300 ease-in-out
//         ${isActive ? "bg-yellow-200 border-yellow-300" : "bg-zinc-100 hover:bg-zinc-200"}
//       `}
//       onPress={onPress}
//     >
//       {<rest.icon />}
//     </Button>
//   </Tooltip>
// );

const EmployeeStatusBadge = ({
  status,
  onClickStatus,
  layout,
}: {
  status: AuthAccountOrganizationEmployeeCooperationStatusChoices | undefined;
  onClickStatus?: () => void;
  layout: "LIST" | "DETAIL";
}) => {
  const statusConfig = {
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.ACTIVE]: {
      text: "Active",
      className: "bg-green-600",
    },
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.AWAITING]: {
      text: "Awaiting Start",
      className: "bg-[#007AFF]",
    },
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.FINISHED]: {
      text: "Finished",
      className: "bg-[#920B3A]",
    },
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.FIRED]: {
      text: "Fired",
      className: "bg-primary-400",
    },
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.SUSPENDED]: {
      text: "Suspended",
      className: "bg-yellow-500",
    },
    [AuthAccountOrganizationEmployeeCooperationStatusChoices.RESIGNED]: {
      text: "Resigned",
      className: "bg-red-400",
    },
  };

  const config = status ? statusConfig[status] : null;

  if (!config) return null;

  if (layout === "DETAIL") {
    return (
      <div className="flex flex-row items-center gap-2">
        <Typography variant="small">Status: </Typography>
        <Button
          size="sm"
          radius="lg"
          className={`text-white ${config.className} py-0.5 px-1.5 h-auto`}
          onPress={onClickStatus}
          endContent={<ArrowRightIcon stroke="#fff" className="rotate-90 w-5 h-5 " />}
        >
          {config.text}
        </Button>
      </div>
    );
  } else {
    return <p className={`p-0.5 pb-0 rounded text-xs text-white ${config.className}`}>{config.text}</p>;
  }
};

const getLocationTypeLabel = (
  type: AuthAccountOrganizationJobPositionLocationTypeChoices | undefined | null,
): string => {
  const locationLabels = {
    [AuthAccountOrganizationJobPositionLocationTypeChoices.GLOBAL]: "Global",
    [AuthAccountOrganizationJobPositionLocationTypeChoices.HYBRID]: "Hybrid",
    [AuthAccountOrganizationJobPositionLocationTypeChoices.LIMITED_AREA]: "On-site (Limited Area)",
    [AuthAccountOrganizationJobPositionLocationTypeChoices.ON_THE_ROAD]: "On the Road",
    [AuthAccountOrganizationJobPositionLocationTypeChoices.PRECISE_LOCATION]: "On-site",
    [AuthAccountOrganizationJobPositionLocationTypeChoices.REMOTE]: "Remote",
  };

  return type ? locationLabels[type] : "";
};

const EmployeeCard = ({ employee, layout = "LIST", refetch }: EmployeeCardProps) => {
  const navigate = useNavigate();
  const { data: avatarUrl } = useFetchFile({
    urlPath: employee?.node?.employee?.profile?.avatar?.file,
    userType: UserTypeEnum.ORG,
  });
  // const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const employeeData = employee?.node;
  const profile = employeeData?.employee;
  const position = employeeData?.cooperations?.[0]?.jobPosition;
  const history = employeeData?.cooperations?.[0];

  const handleGoToDetail = useCallback(() => {
    navigate(routes.postHiring.employeeDetails.replace(":id", employee?.node?.id ?? ""));
  }, [employee?.node?.id, navigate]);

  const Container = layout === "LIST" ? "div" : Card;
  return (
    <>
      <Container
        onClick={handleGoToDetail}
        className="flex cursor-pointer flex-col p-4 border border-zinc-200 rounded-xl transition-all duration-300 ease-in-out"
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start lg:items-center ">
            <div className="flex items-center gap-x-14 gap-y-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Avatar src={avatarUrl} className="w-9 h-9" classNames={{ img: "opacity-100" }} radius="full" />

                <p className="text-sm">{`${profile?.firstName} ${profile?.lastName}`}</p>
                {layout === "LIST" && (
                  <EmployeeStatusBadge status={employeeData?.cooperations?.[0]?.status} layout="LIST" />
                )}
              </div>

              <div className="md:flex flex-col gap-1 text-xs text-zinc-500 hidden">
                <a href={`mailto:${profile?.email}`}>{profile?.email}</a>
                <a href={`tel:${getContactByType(profile?.profile?.contacts, AuthAccountContactTypeChoices.PHONE)}`}>
                  {getContactByType(profile?.profile?.contacts, AuthAccountContactTypeChoices.PHONE)}
                </a>
              </div>

              <div className="hidden md:flex flex-col gap-1 text-xs text-zinc-600">
                <p>{position?.title}</p>
                {history?.startAt && (
                  <p>{`${dayjs(history?.startAt).format("MM/DD/YYYY") ?? "-"} to ${
                    history.endAt ? dayjs(history?.endAt).format("MM/DD/YYYY") : "now"
                  }`}</p>
                )}
              </div>

              {layout === "LIST" && position?.locationType && (
                <p className="p-0.5 bg-zinc-500 rounded text-white text-xs md:flex hidden">
                  {getLocationTypeLabel(position?.locationType)}
                </p>
              )}
            </div>

            {layout === "LIST" && (
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-1">
                  <RankStarIcon className="w-5 h-5 stroke-zinc-600" />
                  <p className="text-sm font-bold">{profile?.profile?.score}</p>
                </div>

                {/* <div className="flex gap-3">
              <IconButton icon={<UserInfoIcon className="w-5 h-5" />} tooltip="Resume" onPress={() => {}} />
              <IconButton
                icon={<RegenerateAiDotIcon className="w-5 h-5" viewBox="0 0 24 24" />}
                tooltip="AI Analysis"
                onPress={() => {}}
              />
              <IconButton
                icon={<EmailIcon className="w-5 h-5" viewBox="0 0 20 20" />}
                tooltip="Messages"
                isActive={isDetailsExpanded}
                onPress={() => setIsDetailsExpanded((prev) => !prev)}
              />
            </div> */}
              </div>
            )}

            {layout === "DETAIL" && (
              <div className="flex items-center gap-2">
                <EmployeeStatusBadge
                  layout={layout}
                  status={employeeData?.cooperations?.[0]?.status}
                  onClickStatus={onOpen}
                />
                {position?.locationType && (
                  <p className="p-0.5 bg-zinc-500 rounded text-white text-xs">
                    {getLocationTypeLabel(position?.locationType)}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-start gap-4">
            <div className="flex flex-col gap-1 text-xs text-zinc-500 md:hidden">
              <a href={`mailto:${profile?.email}`}>{profile?.email}</a>
              <a href={`tel:${getContactByType(profile?.profile?.contacts, AuthAccountContactTypeChoices.PHONE)}`}>
                {getContactByType(profile?.profile?.contacts, AuthAccountContactTypeChoices.PHONE)}
              </a>
            </div>

            <div className="md:hidden flex flex-col gap-1 text-xs text-zinc-600">
              <p>{position?.title}</p>
              {history?.startAt && (
                <p>{`${dayjs(history?.startAt).format("MM/DD/YYYY") ?? "-"} to ${
                  history.endAt ? dayjs(history?.endAt).format("MM/DD/YYYY") : "now"
                }`}</p>
              )}
            </div>

            {layout === "LIST" && position?.locationType && (
              <p className="p-0.5 bg-zinc-500 rounded text-white text-xs flex md:hidden">
                {getLocationTypeLabel(position?.locationType)}
              </p>
            )}
          </div>
        </div>

        <ExpandableSection isExpanded={false} />
      </Container>
      <ChangeStatusModal
        status={employeeData?.cooperations?.[0]?.status}
        cooperationId={employeeData?.cooperations?.[0]?.id}
        isOpen={isOpen}
        onClose={onClose}
        onOpenChange={onOpen}
        refreshData={refetch}
      />
    </>
  );
};

export default EmployeeCard;
